import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const DAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

// Generate slot start-times at `duration` increments across working windows.
function genSlots(duration = 15, windows = [["09:00", "12:00"], ["13:00", "17:00"]]) {
  const step = Math.max(5, Number(duration) || 15);
  const out = [];
  for (const [start, end] of windows) {
    let t = parseInt(start.slice(0, 2), 10) * 60 + parseInt(start.slice(3, 5), 10);
    const stop = parseInt(end.slice(0, 2), 10) * 60 + parseInt(end.slice(3, 5), 10);
    while (t < stop) {
      out.push(`${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`);
      t += step;
    }
  }
  return out;
}

const DEFAULT_SLOTS = genSlots(15); // 15-minute slots per patient

export default function DoctorScheduleSettings() {
  const { user } = useAuth();
  // For admin, allow choosing a target doctor; otherwise default to self.
  const [doctorId, setDoctorId] = useState("");
  const [doctors, setDoctors] = useState([]);
  const [sched, setSched] = useState({
    active_days: ["MON","TUE","WED","THU","FRI"],
    slot_times: DEFAULT_SLOTS,
    slot_duration_minutes: 15,
    blocked_dates: [],
    recurring_blocks: [],
    consultation_fee: 0,
    payment_required: true,
  });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Doctors manage their own; admins and reception can manage any doctor's schedule.
  const canManageOthers = ["admin", "hospital_admin", "reception"].includes(user?.role);
  const isDoctor = user?.role === "doctor";

  // Load list of doctors for managers (admin / reception); doctors default to self.
  useEffect(() => {
    (async () => {
      try {
        if (canManageOthers) {
          const { data } = await api.get("/doctors");
          const list = data || [];
          setDoctors(list);
          if (list.length && !doctorId) setDoctorId(list[0].id);
        } else {
          setDoctorId(user?.id || "");
        }
      } catch (e) {
        toast.error(formatErr(e?.response?.data?.detail || e));
      }
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!doctorId) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/doctor-schedules/${doctorId}`);
        setSched({
          active_days: data.active_days || ["MON","TUE","WED","THU","FRI"],
          slot_times: data.slot_times || DEFAULT_SLOTS,
          slot_duration_minutes: data.slot_duration_minutes || 15,
          blocked_dates: data.blocked_dates || [],
          recurring_blocks: data.recurring_blocks || [],
          consultation_fee: data.consultation_fee || 0,
          payment_required: data.payment_required !== false,
        });
      } catch (e) {
        toast.error(formatErr(e?.response?.data?.detail || e));
      } finally { setLoading(false); }
    })();
  }, [doctorId]);

  const toggleDay = (d) => setSched((s) => ({
    ...s,
    active_days: s.active_days.includes(d) ? s.active_days.filter((x) => x !== d) : [...s.active_days, d],
  }));

  const editSlots = (txt) => setSched((s) => ({
    ...s,
    slot_times: txt.split(",").map((x) => x.trim()).filter(Boolean),
  }));

  const addBlock = () => setSched((s) => ({
    ...s, recurring_blocks: [...s.recurring_blocks, { day_of_week: "MON", start: "12:00", end: "13:00", label: "" }],
  }));
  const updateBlock = (i, patch) => setSched((s) => {
    const arr = [...s.recurring_blocks];
    arr[i] = { ...arr[i], ...patch };
    return { ...s, recurring_blocks: arr };
  });
  const removeBlock = (i) => setSched((s) => ({ ...s, recurring_blocks: s.recurring_blocks.filter((_, idx) => idx !== i) }));

  const addBlockedDate = (d) => {
    if (!d) return;
    setSched((s) => s.blocked_dates.includes(d) ? s : ({ ...s, blocked_dates: [...s.blocked_dates, d] }));
  };
  const removeBlockedDate = (d) => setSched((s) => ({ ...s, blocked_dates: s.blocked_dates.filter((x) => x !== d) }));

  const save = async () => {
    if (!doctorId) return;
    setBusy(true);
    try {
      await api.put(`/doctor-schedules/${doctorId}`, sched);
      toast.success("Schedule saved");
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally { setBusy(false); }
  };

  if (loading && !doctorId) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div className="p-6 max-w-4xl" data-testid="doctor-schedule-page">
      <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Work Sans" }}>Doctor Schedule</h1>
      <p className="text-sm text-slate-500 mt-1">Set active days, slot times, recurring blocks (e.g. lunch), and blocked dates.</p>
      <div className="mt-2 text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
        This schedule syncs across <b>every clinic this doctor is affiliated with</b> — bookings at one clinic
        block the same time at the others, so a doctor is never double-booked.
      </div>

      {canManageOthers && doctors.length > 0 && (
        <div className="mt-4 max-w-md">
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Doctor</label>
          <select value={doctorId} onChange={(e) => setDoctorId(e.target.value)} className="mt-1 w-full h-11 px-3 rounded-lg border border-slate-300" data-testid="schedule-doctor">
            {doctors.map((d) => (<option key={d.id} value={d.id}>{d.name}</option>))}
          </select>
        </div>
      )}

      <Card className="mt-5 p-5 space-y-5">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Active days</div>
          <div className="flex flex-wrap gap-2">
            {DAYS.map((d) => (
              <button key={d} type="button" onClick={() => toggleDay(d)}
                className={`h-9 px-3 rounded-lg border text-sm font-semibold ${sched.active_days.includes(d) ? "bg-blue-700 border-blue-700 text-white" : "bg-white border-slate-300 text-slate-600"}`}
                data-testid={`day-${d}`}>{d}</button>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Slot times (comma-separated, HH:MM)</div>
            <Button
              size="sm"
              variant="outline"
              type="button"
              onClick={() => setSched((s) => ({ ...s, slot_times: genSlots(s.slot_duration_minutes || 15) }))}
              data-testid="regen-slots"
            >
              Regenerate {sched.slot_duration_minutes || 15}-min slots
            </Button>
          </div>
          <Input value={(sched.slot_times || []).join(", ")} onChange={(e) => editSlots(e.target.value)} className="mt-2 h-11" data-testid="slot-times" />
          <div className="text-xs text-slate-400 mt-1">{(sched.slot_times || []).length} slots · patients only ever see times that are still open.</div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Slot duration (min)</div>
            <Input type="number" min="5" value={sched.slot_duration_minutes} onChange={(e) => setSched({ ...sched, slot_duration_minutes: Number(e.target.value || 30) })} className="mt-2 h-11" />
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Consultation fee (PHP)</div>
            <Input type="number" min="0" value={sched.consultation_fee} onChange={(e) => setSched({ ...sched, consultation_fee: Number(e.target.value || 0) })} className="mt-2 h-11" />
          </div>
        </div>

        <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
          <input
            id="payment-required"
            type="checkbox"
            checked={!!sched.payment_required}
            onChange={(e) => setSched({ ...sched, payment_required: e.target.checked })}
            className="h-4 w-4 accent-blue-700"
            data-testid="payment-required-toggle"
          />
          <label htmlFor="payment-required" className="text-sm">
            <span className="font-semibold text-slate-800">Require upfront payment via QR for online bookings</span>
            <div className="text-xs text-slate-500 mt-0.5">Turn this off to waive payment — reservations will be auto-confirmed and patients skip the QR step.</div>
          </label>
        </div>

        {/* Recurring blocks */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Recurring blocks (lunch, meeting, etc.)</div>
            <Button size="sm" variant="outline" onClick={addBlock} data-testid="add-block"><Plus size={12} className="mr-1" />Add</Button>
          </div>
          {sched.recurring_blocks.length === 0 && <div className="text-xs text-slate-400">No recurring blocks.</div>}
          {sched.recurring_blocks.map((b, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center mt-2" data-testid={`block-row-${i}`}>
              <select value={b.day_of_week} onChange={(e) => updateBlock(i, { day_of_week: e.target.value })} className="col-span-2 h-10 rounded-lg border border-slate-300 px-2 text-sm">
                {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              <Input type="time" value={b.start} onChange={(e) => updateBlock(i, { start: e.target.value })} className="col-span-3 h-10" />
              <Input type="time" value={b.end} onChange={(e) => updateBlock(i, { end: e.target.value })} className="col-span-3 h-10" />
              <Input value={b.label || ""} onChange={(e) => updateBlock(i, { label: e.target.value })} placeholder="Label (lunch, etc.)" className="col-span-3 h-10" />
              <Button size="icon" variant="ghost" onClick={() => removeBlock(i)} className="text-red-600" data-testid={`remove-block-${i}`}><Trash2 size={14} /></Button>
            </div>
          ))}
        </div>

        {/* Blocked dates */}
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Blocked dates (vacation, holiday)</div>
          <div className="flex items-center gap-2">
            <Input type="date" onChange={(e) => { addBlockedDate(e.target.value); e.target.value = ""; }} className="h-10 max-w-xs" data-testid="blocked-date-input" />
          </div>
          {sched.blocked_dates.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {sched.blocked_dates.map((d) => (
                <span key={d} className="inline-flex items-center gap-1 bg-red-50 text-red-700 border border-red-200 rounded-full px-3 py-1 text-xs font-medium" data-testid={`blocked-date-${d}`}>
                  {d}
                  <button type="button" onClick={() => removeBlockedDate(d)} className="ml-1 text-red-600">×</button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={busy} className="bg-blue-700 hover:bg-blue-800" data-testid="schedule-save"><Save size={14} className="mr-1" />Save schedule</Button>
        </div>
      </Card>
    </div>
  );
}
